import {css, forBrands, styled, Brands} from "@core-ui/react-stitch";
import {Button} from "@core-ui/button";
import {
  Color,
  ComposableButtonBaseProps,
  ComposableButtonStyleFn,
  Variant,
} from "../types";
import {
  setBorder,
  setBorderButtonStyles,
  setButtonHeight,
  setFontSize,
  interactiveStyles as interactiveStylesLegacy,
  setChevronButtonStyles,
  setFlatButtonStyles,
  setFontStyles,
  setFullWidth,
  setOutlineButtonStyles,
  setSolidButtonStyles,
  setUnderlineButtonStyles,
} from "./composable-button-styles";
import {getComposableButtonStylesPerBrand, setTextAlign} from "../styles";
import {getInteractiveStyles} from "../config/interactive-styles";
import {defaultStyle} from "../styles/helpers";

const removeDefaultButtonStyles = css`
  // Remove some standard appearance issues,
  // like border-radius on Firefox mobile
  appearance: none;
  border-radius: 0;
`;

const setStyles: ComposableButtonStyleFn = ({
  color: colorProp = Color.dark,
  crossBrand,
  customColors = {
    backgroundColor: "#000",
    foregroundColor: "#fff",
  },
  variant,
  theme,
  size,
}) => {
  switch (variant) {
    case Variant.border:
      return setBorderButtonStyles(theme, crossBrand, colorProp, customColors);
    case Variant.flat:
      return setFlatButtonStyles(theme, crossBrand, colorProp, customColors);
    case Variant.outline:
      return setOutlineButtonStyles(theme, crossBrand, colorProp, customColors);
    case Variant.underline:
      return setUnderlineButtonStyles(
        theme,
        crossBrand,
        colorProp,
        customColors
      );
    case Variant.chevron:
      return setChevronButtonStyles(
        theme,
        size,
        crossBrand,
        colorProp,
        customColors
      );
    default:
      return setSolidButtonStyles(theme, crossBrand, colorProp, customColors);
  }
};

export const ComposableButtonBase = styled(Button)<ComposableButtonBaseProps>`
  ${removeDefaultButtonStyles};
  padding: 0;
  border: 0;

  &:focus {
    outline: none;
  }
  // Put padding *inside* the width
  box-sizing: border-box;

  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: normal;
  text-transform: ${({capitalization = "uppercase"}) => capitalization};
  cursor: pointer;
  vertical-align: middle;

  // Simple transitions for hover states
  transition: all 250ms ease-in-out;

  // Put padding *inside* the width
  box-sizing: border-box;

  // Solid type is default
  border: ${({theme, crossBrand}) =>
      defaultStyle(theme, crossBrand).borderWidth}px
    solid transparent;

  // Set border-radius
  ${({theme, crossBrand, pillShape, roundedCorners}) => {
    if (pillShape) return "border-radius: 99em;";
    if (roundedCorners) return `border-radius: ${theme.borderRadius.button};`;
    return forBrands(
      theme,
      {
        at: {
          borderRadius: "8px",
        },
        on: {
          borderRadius: "8px",
        },
        crossBrand: {borderRadius: "0"},
        default: {borderRadius: "0"},
      },
      crossBrand
    );
  }}

  ${(props) => {
    if (
      !props.crossBrand &&
      [
        Brands.OldNavy,
        Brands.Athleta,
        Brands.Gap,
        Brands.GapFactoryStore,
      ].includes(props.theme.brand)
    ) {
      return css`
        ${getComposableButtonStylesPerBrand(props)}
        ${setBorder(props)}
        ${props.interactiveStyles && getInteractiveStyles(props)}
      `;
    }
    return css`
      ${setTextAlign(props)}
      ${setFullWidth(props)}
      ${setButtonHeight(props)}
      ${setFontSize(props)}
      ${setFontStyles(props)}
      ${setStyles(props)}
      ${setBorder(props)}
      ${interactiveStylesLegacy(props)}
    `;
  }};
`;
